import { useEffect, useState } from 'react';
import { isBefore } from '@ninja/utilities';
import { getTzTodayDate, parseIsoDateTime } from '@ninja/utilities/dist/DateUtils';
import { QuoteSort } from '../../../common';
import { useAppPlatformContextOptions } from '../../../contexts/app';
import { JourneyDetails } from '../../../server';
import usePlanTypeSelection from './hooks/usePlanTypeSelection';
import { QuoteListUrlParam, useQuoteFilterForm } from './quote-sort-filters';
import { QuoteFiltersFromUrl } from './quote-sort-filters/filter-schema/helpers';
import { QuotePlanSchema } from './server/map-quotes';

export const useQuoteDetails = ({
	quotePlans,
	onRouterReplace,
	quoteListUrlParams,
	onNavigation,
	journey,
	onEffectiveDateError,
}: {
	quotePlans: QuotePlanSchema[];
	onRouterReplace: (urlParam: QuoteListUrlParam) => void;
	quoteListUrlParams: QuoteFiltersFromUrl;
	onNavigation: (quotePlanReferenceNumber: string) => Promise<void>;
	onEffectiveDateError: () => Promise<void>;
	journey: JourneyDetails;
}) => {
	const { locale } = useAppPlatformContextOptions();
	const { planTypeId, sortId } = quoteListUrlParams;
	const planType = typeof planTypeId === 'string' ? Number(planTypeId) : 0;
	const [selectedPlanType, setSelectedPlanType] = useState(planType);
	const [selectedSort, setSelectedSort] = useState((sortId as QuoteSort) ?? QuoteSort.PremiumLowHigh);
	const { filterForm, filterFieldSchema, applySortFilter, allQualifiedQuotes, setAllQualifiedQuotes } =
		useQuoteFilterForm({
			originalQuotes: quotePlans,
			onRouterReplace,
			initialValuesFromUrl: quoteListUrlParams,
			selectedPlanType,
			selectedSort,
		});

	useEffect(() => {
		applySortFilter(selectedPlanType, selectedSort);
	}, []);

	useEffect(() => {
		applySortFilter(selectedPlanType, selectedSort, quotePlans);
	}, [quotePlans, locale]);

	const filterWithPlanType = (planTypeId: number) => {
		setSelectedPlanType(planTypeId);
		applySortFilter(planTypeId, selectedSort);
	};

	const onSort = (sortId: QuoteSort) => {
		setSelectedSort(sortId);
		applySortFilter(selectedPlanType, sortId);
	};

	const onSelectedQuote = async (selectedQuote: QuotePlanSchema) => {
		const effectiveDateFrom = parseIsoDateTime(journey.effectiveDateFrom);

		if (effectiveDateFrom && isBefore(effectiveDateFrom, getTzTodayDate())) {
			onEffectiveDateError();

			return;
		}

		await onNavigation(selectedQuote.quotePlanReferenceNumber);
	};

	const { planTypes } = usePlanTypeSelection({ allPlanQuotes: quotePlans });

	const onSelectPlanFromGroupedIc = (selectedActivePlan: number | undefined) => {
		const selectedPlan = allQualifiedQuotes.map((quote) => {
			const planToUpdate = quote.quotePlans.find((plan) => plan.planId === selectedActivePlan);

			if (planToUpdate) {
				return {
					...quote,
					selectedActivePlan: planToUpdate.planId,
				};
			}

			return quote;
		});

		setAllQualifiedQuotes(selectedPlan);
	};

	return {
		fieldSchema: filterFieldSchema,
		onSelectPlanFromGroupedIc,
		allQualifiedQuotes,
		filterWithPlanType,
		selectedPlanType,
		onSelectedQuote,
		selectedSort,
		filterForm,
		planTypes,
		onSort,
	};
};
